// Stage 72 — the global shortcut set.
//
// The spine every page relies on: the command palette, the ? overlay, the
// g-chord navigation set, and the admin agent toggle (migrated from Stage
// 71 / Stage 30's hand-rolled app-shell keydown checks). Registered once
// from cos-app-shell's connectedCallback; returns an unregister-all fn for
// disconnectedCallback.
//
// Shell-specific actions (open palette / overlay / agent panel) are injected
// as callbacks so this module stays free of the shell instance; navigation
// uses the Stage 71 `navigate()` window-event helper that app.ts already
// routes through its client-side router.

import { shortcutRegistry } from './registry.js';
import { navigate } from '../commands/navigate.js';

export interface GlobalShortcutDeps {
	openPalette: () => void;
	openShortcutOverlay: () => void;
	toggleAgentPanel: () => void;
	closeOverlays: () => void;
	isAdmin: () => boolean;
	/** Communico staff (userLevel === 1) — gates the feedback-widget shortcut. */
	isStaff: () => boolean;
}

interface NavSpec {
	keys: string;
	path: string;
	label: string;
}

const NAV: NavSpec[] = [
	{ keys: 'g i', path: '/inbox', label: 'Go to Inbox' },
	{ keys: 'g p', path: '/partners', label: 'Go to Partners' },
	{ keys: 'g t', path: '/tickets', label: 'Go to Tickets' },
	{ keys: 'g c', path: '/contacts', label: 'Go to Contacts' },
	{ keys: 'g m', path: '/mail', label: 'Go to Mail' },
	{ keys: 'g h', path: '/chat', label: 'Go to Chat' },
	{ keys: 'g d', path: '/dashboard', label: 'Go to Dashboard' },
	{ keys: 'g n', path: '/notifications', label: 'Go to Notifications' },
];

/**
 * Register the global shortcut set. Returns a fn that unregisters all of
 * them (call from the shell's disconnectedCallback).
 */
export function registerGlobalShortcuts(deps: GlobalShortcutDeps): () => void {
	const offs: Array<() => void> = [];
	const reg = (b: Parameters<typeof shortcutRegistry.register>[0]) =>
		offs.push(shortcutRegistry.register(b));

	reg({
		id: 'global.cmdK',
		keys: 'Cmd+K',
		scope: 'global',
		description: 'Open command palette',
		category: 'Palette',
		allowInInputs: true, // escape valve — works even while typing
		handler: () => deps.openPalette(),
	});

	reg({
		id: 'global.help',
		keys: '?',
		scope: 'global',
		description: 'Show keyboard shortcuts',
		category: 'Help',
		// NOT allowInInputs — '?' is a printable character (D-2 in the
		// design-decisions doc): typing "config?" must not pop the overlay.
		handler: () => deps.openShortcutOverlay(),
	});

	reg({
		id: 'global.esc',
		keys: 'Esc',
		scope: 'global',
		description: 'Close the active overlay or side panel',
		category: 'Help',
		allowInInputs: true, // escape valve
		preventDefault: false, // let native Esc (IME, native menus) still work
		handler: () => deps.closeOverlays(),
	});

	for (const nav of NAV) {
		reg({
			id: `global.nav.${nav.path.slice(1)}`,
			keys: nav.keys,
			scope: 'global',
			description: nav.label,
			category: 'Navigation',
			handler: () => navigate(nav.path),
		});
	}

	// Cmd+/ — admin agent panel (migrated from Stage 30's app-shell check).
	// Admin-only, so only register it for admins — keeps it out of the ?
	// overlay for everyone else.
	if (deps.isAdmin()) {
		reg({
			id: 'global.agent',
			keys: 'Cmd+/',
			scope: 'global',
			description: 'Toggle the agent panel',
			category: 'Palette',
			allowInInputs: true,
			handler: () => deps.toggleAgentPanel(),
		});
	}

	// Staff feedback / issue-capture platform — dispatches a window event
	// (the cos:* cross-component convention) rather than calling the shell
	// directly, since the widget itself lives in the header, not this module.
	// Staff-only, mirroring the agent-panel admin-only gate.
	if (deps.isStaff()) {
		reg({
			id: 'global.feedback',
			keys: 'Cmd+Shift+F',
			scope: 'global',
			description: 'Send feedback',
			category: 'Palette',
			handler: () => window.dispatchEvent(new CustomEvent('cos:feedback:open')),
		});
	}

	return () => {
		for (const off of offs) off();
	};
}
